import { HTMLProps, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PensionContext } from '../../../../contexts/pension-robot/PensionContext';
import { isPensionCalculatedTogether } from '../../../../utils/functions';
import { useGetPerMonthFactorValues } from "../../../../hooks/useGetPerMonthFactorValues";
import { toPrice } from '../../../../utils/parsers';
import { LoadingStateWrapper } from '../../../general/LoadingStateWrapper';
import { DEFAULT_PENSION_AGE, LAST_SECTION_INDEX, MIN_AGE_CLIENT } from '../../../../config/constants';
import { useNetCalculation } from '../../../../hooks/netGrossCalculations';
import { ShortageWarning } from '../RetirementWarning';

export const usePensionDateOrder = () => {
    const {
        parameters: { personalInfo},
    } = useContext(PensionContext);

    const selfYearsToPension = (personalInfo.self?.pensionAge ?? DEFAULT_PENSION_AGE) - (personalInfo.self?.age ?? 0);
    const partnerYearsToPension = (personalInfo.partner?.pensionAge ?? DEFAULT_PENSION_AGE) - (personalInfo.partner?.age ?? 0 );
    
    if (selfYearsToPension === partnerYearsToPension) return "samePensionDate";
    if (selfYearsToPension < partnerYearsToPension) return "selfEarlierPensionDate";
    return "partnerEarlierPensionDate";
};

export const ShortageTopText: React.FC<{
    amountNeedToReachPensionGoal: number,
    aowMissing: boolean,
} & HTMLProps<HTMLDivElement>> = ({ amountNeedToReachPensionGoal, aowMissing }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { personalInfo, expensesInfo },
    } = useContext(PensionContext);

    const pensionDateOrder = usePensionDateOrder();

    const isCalculatedTogether = isPensionCalculatedTogether(personalInfo);

    const retirementForecastQuery = useGetPerMonthFactorValues(personalInfo.self?.age ?? MIN_AGE_CLIENT);
    const retirementForecastQueryPartner = useGetPerMonthFactorValues(personalInfo.partner?.age ?? MIN_AGE_CLIENT);

    const netValuesAtRetirementAgeOfSelf = useNetCalculation(LAST_SECTION_INDEX, false);
    const netValuesAtRetirementAgeOfPartner = useNetCalculation(LAST_SECTION_INDEX, true);

    const incomeExtraRequired = toPrice(amountNeedToReachPensionGoal, {
        onlyFullEuros: true,
        withCurrencySymbol: true,
    })

    const netDiffAtAgeOfSelfRetirement = netValuesAtRetirementAgeOfSelf.netIncome.total.monthly - (expensesInfo?.goal ?? 0);
    const netDiffAtAgeOfSelfRetirementAsPrice = toPrice(Math.abs(netDiffAtAgeOfSelfRetirement), {
        onlyFullEuros: true,
        withCurrencySymbol: true,
    })

    const getWithPartnerText = () => {

        const netDiffAtAgeOfPartnerRetirement = netValuesAtRetirementAgeOfPartner.netIncome.total.monthly - (expensesInfo?.goal ?? 0);
        const netDiffAtAgeOfPartnerRetirementAsPrice = toPrice(Math.abs(netDiffAtAgeOfPartnerRetirement), {
            onlyFullEuros: true,
            withCurrencySymbol: true,
        })

        const selfShortage = netDiffAtAgeOfSelfRetirement <= 0;
        const partnerShortage = netDiffAtAgeOfPartnerRetirement <= 0;

        const aowTextIfMissingAow = aowMissing
            ? t('pension-warning-aow-missing-joined-shortage')
            : '';

        // 1. Tekort vanaf beide pensioenleeftijden
        const bothShortage = selfShortage && partnerShortage;
        if (bothShortage){
            // 1c: Klant en partner hebben zelfde pensioen datum
            if (pensionDateOrder === "samePensionDate"){
                return t('pension-result-alert-both-shortage-same-pension-date-1c', {
                    shortagePerMoth: netDiffAtAgeOfSelfRetirementAsPrice,
                    incomeExtraRequired,
                    aowTextIfMissingAow,
                })
            }
            if (pensionDateOrder === "selfEarlierPensionDate"){
                // 1a : Klant gaat eerder met pensioen dan partner
                return t('pension-result-alert-both-shortage-selected-self-as-pension-date-1a', {
                    shortageOnSelfPensionDate: netDiffAtAgeOfSelfRetirementAsPrice,
                    shortageOnPartnerPensionDate: netDiffAtAgeOfPartnerRetirementAsPrice,
                    incomeExtraRequired,
                    aowTextIfMissingAow
                })
            }

            // 1b: Partner gaat eerder met pensioen dan klant
            return t("pension-result-alert-both-shortage-selected-partner-as-pension-date-1b", {
                shortageOnSelfPensionDate: netDiffAtAgeOfSelfRetirementAsPrice,
                shortageOnPartnerPensionDate: netDiffAtAgeOfPartnerRetirementAsPrice,
                incomeExtraRequired,
                aowTextIfMissingAow
            })
        }

        // 2. Tekort vanaf pensioenleeftijd klant en overschot vanaf pensioenleeftijd partner
        const selfShortagePartnerSurplus = selfShortage && !partnerShortage;
        if (selfShortagePartnerSurplus){
            if (pensionDateOrder === "selfEarlierPensionDate"){
                // 2a: Klant gaat eerder met pensioen dan partner
                return t("pension-result-alert-self-shortage-selected-self-as-pension-date-2a",{
                    shortageOnSelfPensionDate: netDiffAtAgeOfSelfRetirementAsPrice,
                    surplusOnPartnerPensionDate: netDiffAtAgeOfPartnerRetirementAsPrice,
                    incomeExtraRequired,
                    aowTextIfMissingAow
                })
            }
            // 2b: Partner gaat eerder met pensioen dan klant
            return t("pension-result-alert-self-shortage-selected-partner-as-pension-date-2b",{
                shortageOnSelfPensionDate: netDiffAtAgeOfSelfRetirementAsPrice,
                surplusOnPartnerPensionDate: netDiffAtAgeOfPartnerRetirementAsPrice,
                incomeExtraRequired,
                aowTextIfMissingAow
            })
        }

        // 3. Overschot vanaf pensioenleeftijd klant en tekort vanaf pensioenleeftijd partner
        const selfSurplusPartnerShortage = !selfShortage && partnerShortage;
        if(selfSurplusPartnerShortage){
            if (pensionDateOrder === "selfEarlierPensionDate"){
                // 3a: Klant gaat eerder met pensioen dan partner
                return t("pension-result-alert-partner-shortage-selected-self-as-pension-date-3a", {
                    surplusOnSelfPensionDate: netDiffAtAgeOfSelfRetirementAsPrice,
                    shortageOnPartnerPensionDate: netDiffAtAgeOfPartnerRetirementAsPrice,
                    incomeExtraRequired,
                    aowTextIfMissingAow
                })
            }
            // 3b: Partner gaat eerder met pensioen dan klant
            return t("pension-result-alert-partner-shortage-selected-partner-as-pension-date-3b", {
                surplusOnSelfPensionDate: netDiffAtAgeOfSelfRetirementAsPrice,
                shortageOnPartnerPensionDate: netDiffAtAgeOfPartnerRetirementAsPrice,
                incomeExtraRequired,
                aowTextIfMissingAow
            })
        }
    }

    const getWithoutPartnerText = () => {
        const aowTextIfMissingAowSingle = aowMissing
            ? t('pension-warning-aow-missing-single-shortage')
            : '';
        return t("pension-cocktail-fill-screen-results-shortage-text", {
            shortage: netDiffAtAgeOfSelfRetirementAsPrice,
            incomeExtraRequired,
            aowTextIfMissingAow: aowTextIfMissingAowSingle
        })
    }

    const getText = () => {
        return isCalculatedTogether
            ? getWithPartnerText() + t('pension-cokctail-fill-screen-results-other-options-text')
            : getWithoutPartnerText()
    }

    return (
        <LoadingStateWrapper
            isLoading={retirementForecastQuery.isLoading || retirementForecastQueryPartner.isLoading}
            isError={retirementForecastQuery.isError || retirementForecastQueryPartner.isError}
            isIdle={retirementForecastQuery.isIdle || retirementForecastQueryPartner.isIdle}
            isFetching={retirementForecastQuery.isFetching || retirementForecastQueryPartner.isFetching}
        >
            <ShortageWarning text={getText()}/>
        </LoadingStateWrapper>
    );
};
